/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Mantine
import {
	Box,
	Title,
	Radio as NativeRadio,
	Group,
	Stack,
	LoadingOverlay,
	Tooltip,
} from '@mantine/core';
import {
	useElementSize,
	useMediaQuery,
} from '@mantine/hooks';

// Components
import {
	Select,
	Button,
	Text,
	TextInputV2,
} from '@components/atoms';
import { DropzoneMultipleFiles } from '@components/molecules/DropzoneMultipleFiles';

import { RichTextEditor } from '@components/molecules/RichTextEditor';

// Styles
import classes from './AddMeasurabilityProjectForm.module.css';

// Utils
import { convertArrayToLabelValue } from '@utils/functions';

// I18n
import { useTranslation } from 'react-i18next';

import {
	Divider,
	FileInput,
	NumericInput,
	Radio,
} from '@components/atoms';

// Constants
import { UNITS_PROJECT } from '@config/constants/units-project';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';

// Helpers
import { getCurrencyUnit } from '@utils/currency-units';

// Hooks
import { useMeasurabilityProjectCreate } from '@features/catalogs/meausrability-projects/list/hooks/useMeasurabilityProjectCreate';
import { useAllProducers } from '@api/get-all-producers';
import { useOrganizations } from 'shared/api/get-organizations';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddMeasurabilityProjectForm = (props: {
	form: any;
	onCancel: () => void;
	onBack: () => void;
	isPreviousMeasurabilityProjectDetails: boolean;
}) => {
	const {
		isLoadingPublicTechnicalDocuments,
		setIsLoadingPublicTechnicalDocuments,
		uploadMediaLogo,
		uploadMediaCoverUrl,
		handleFileLogo,
		handleFileCoverUrl,
		handleSubmitForm,
		convertArrayTechnologiesToLabelValue,
		addMeasurabilityProject,
		handleUploadFiles,
		handleReloadFiles,
	} = useMeasurabilityProjectCreate({ form: props.form });
	const [, setProducerId] = useState('');
	const { ref, width } = useElementSize();
	const { t } = useTranslation();
	const { data: organizations } = useOrganizations();

	const { data: producers } = useAllProducers();

	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const handleChangeSupportedBy = (
		value: string,
		form: any
	) => {
		form.setFieldValue('supportedBy', value);
	};

	useEffect(() => {
		if (addMeasurabilityProject.isSuccess) {
			props.onBack();
		}
	}, [addMeasurabilityProject.isSuccess]);

	const { onChange: onChangeSite, ...entityProps } =
		props.form.getInputProps('entity');

	const handleChangeEntity = (value: string) => {
		props.form.setFieldValue('entity', value);
		props.form.setFieldValue('project', null);
		setProducerId(value);
	};

	const { error: errorLogo } =
		props.form.getInputProps('logo');

	const { error: errorCoverUrl } =
		props.form.getInputProps('cover');

	const {
		onChange: onChangeSupportedBy,
		...supportedByProps
	} = props.form.getInputProps('supportedBy');

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			<form className={classes.containerForm}>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{t('global.projects')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('measurabilities.addMeasurabilities')}
					</Text>
				</Box>
				<NativeRadio.Group
					value={'existingProducer'}
					name="addProject"
					withAsterisk
					onChange={() => {}}
				>
					<Group gap={'47px'}>
						<Radio
							variant="outline"
							size={isBigScreen ? 'sm' : 'xs'}
							label={t('global.selectAnExistingProducer')}
							value={'existingProducer'}
						/>
						<Radio
							disabled
							variant="outline"
							size={isBigScreen ? 'sm' : 'xs'}
							label={t('global.addNewProducer')}
							value={'newProducer'}
						/>
					</Group>
				</NativeRadio.Group>
				{/* Row for Producer */}
				<Box ref={ref} className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.producer')}
							withAsterisk
							data={convertArrayToLabelValue(producers)}
							placeholder={t('global.selectProducer')}
							onChange={handleChangeEntity}
							{...entityProps}
						/>
					</Box>
				</Box>
				{/* Row for organization */}
				{props.form.getValues().entity !== '' &&
					props.form.getValues().entity !== null && (
						<Box ref={ref} className={classes.row}>
							<Box className={classes.containerInput}>
								<Select
									size={isBigScreen ? 'md' : 'sm'}
									label={t('global.supportedBy')}
									data={convertArrayToLabelValue(
										organizations
									)}
									placeholder={t(
										'global.selectOrganization'
									)}
									onChange={(entity: string) => {
										handleChangeSupportedBy(
											entity,
											props.form
										);
									}}
									{...supportedByProps}
								/>
							</Box>
						</Box>
					)}
				<Divider size={'xs'} />
				{props.form.getValues().entity !== '' &&
					props.form.getValues().entity !== null && (
						<Stack pos="relative">
							<LoadingOverlay
								visible={
									uploadMediaLogo.isLoading ||
									uploadMediaCoverUrl.isLoading ||
									isLoadingPublicTechnicalDocuments
								}
								zIndex={1000}
								overlayProps={{ radius: 'sm', blur: 1 }}
							/>
							<Box className={classes.rowFcDetails}>
								<Title className={classes.titleFcDetails}>
									{t('Project details')}
								</Title>
							</Box>

							{/* Row for name */}
							<Box className={classes.containerInput}>
								<TextInputV2
									withAsterisk
									size={isBigScreen ? 'md' : 'sm'}
									label={t('global.name')}
									placeholder={t(
										'placeholders.projectName'
									)}
									{...props.form.getInputProps('name')}
								/>
							</Box>
							{/* Row for Country & Place */}
							<Box className={classes.row}>
								<Box className={classes.containerInput}>
									<Select
										withAsterisk
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.country')}
										data={EUROPEAN_COUNTRIES}
										placeholder={t(
											'placeholders.selectCountry'
										)}
										{...props.form.getInputProps(
											'countryCode'
										)}
									/>
								</Box>
								<Box className={classes.containerInput}>
									<TextInputV2
										disabled={
											!props.form.values.countryCode
										}
										withAsterisk
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.place')}
										placeholder={t('global.place')}
										{...props.form.getInputProps('place')}
									/>
								</Box>
							</Box>

							{/* Row for Unit & Technology */}
							<Box className={classes.row}>
								<Box className={classes.containerInput}>
									<Select
										withAsterisk
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.labels.unit')}
										data={UNITS_PROJECT}
										placeholder={t(
											'placeholders.selectUnit'
										)}
										{...props.form.getInputProps('unit')}
									/>
								</Box>
								<Box className={classes.containerInput}>
									<Select
										withAsterisk
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.technology')}
										data={convertArrayTechnologiesToLabelValue(
											PROJECT_TECHNOLOGIES
										)}
										placeholder={t(
											'placeholders.selectTechnology'
										)}
										{...props.form.getInputProps(
											'technology'
										)}
									/>
								</Box>
							</Box>
							{/* Row for sellPrice & costPrice */}
							<Box className={classes.row}>
								<Box className={classes.containerInput}>
									<NumericInput
										withAsterisk
										max={9999}
										decimalScale={2}
										unit={t('global.sellPriceUnit')}
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.sellPriceLabel')}
										defaultValue={0}
										{...props.form.getInputProps(
											'sellPrice_europertco2eq'
										)}
									/>
								</Box>
								<Box className={classes.containerInput}>
									<NumericInput
										max={999}
										decimalScale={2}
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.costPriceLabel')}
										defaultValue={0}
										unit={getCurrencyUnit('EUR')}
										{...props.form.getInputProps(
											'costPrice_europertco2eq'
										)}
									/>
								</Box>
							</Box>
							{/* Row for logo & cover */}
							<Box className={classes.row}>
								<Box
									className={classes.containerFileInput}
								>
									<FileInput
										maxsize={20}
										accept="image/png,image/jpeg"
										error={errorLogo}
										clearable
										onChange={(file: File | null) => {
											handleFileLogo(file, props.form);
										}}
										label={t('global.logo')}
										size={isBigScreen ? 'md' : 'sm'}
									/>
								</Box>
								<Box
									className={classes.containerFileInput}
								>
									<FileInput
										maxsize={20}
										accept="image/png,image/jpeg"
										withAsterisk
										clearable
										onChange={(file: File | null) => {
											handleFileCoverUrl(
												file,
												props.form
											);
										}}
										label={t('global.coverImage')}
										size={isBigScreen ? 'md' : 'sm'}
										error={errorCoverUrl}
									/>
								</Box>
							</Box>
							{/* Row for Dropzone multiplefiles */}
							<Tooltip
								label={t('global.accessibleToPublic')}
							>
								<Box className={classes.row}>
									<DropzoneMultipleFiles
										isPublicFile={true}
										entity={props.form.getValues().entity}
										handleUpload={(files: {
											filename: string;
											id: string;
										}) => {
											handleUploadFiles(
												files,
												props.form
											);
										}}
										handleReload={(filteredFiles: {
											filename: string;
											id: string;
										}) => {
											handleReloadFiles(
												filteredFiles,
												props.form
											);
										}}
										label={t('global.technicalDocuments')}
										getUploadStatus={(
											status: boolean
										) => {
											if (!status) {
												setIsLoadingPublicTechnicalDocuments(
													true
												);
											}
											if (status) {
												setIsLoadingPublicTechnicalDocuments(
													false
												);
											}
										}}
									/>
								</Box>
							</Tooltip>
							<Divider size={'xs'} />
							{/* Row for Linkedin & X */}
							<Box className={classes.row}>
								<Box className={classes.containerInput}>
									<TextInputV2
										size={isBigScreen ? 'md' : 'sm'}
										label={t('X Url')}
										placeholder={'https://www.x.com/'}
										{...props.form.getInputProps(
											'twitter'
										)}
									/>
								</Box>
								<Box className={classes.containerInput}>
									<TextInputV2
										size={isBigScreen ? 'md' : 'sm'}
										label={t('Linkedin Url')}
										placeholder={
											'https://www.linkedin.com/'
										}
										{...props.form.getInputProps(
											'linkedin'
										)}
									/>
								</Box>
							</Box>
							{/* Row for website */}
							<Box className={classes.row}>
								<Box className={classes.containerInput}>
									<TextInputV2
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.website')}
										placeholder={
											'https://www.example.com/'
										}
										{...props.form.getInputProps(
											'website'
										)}
									/>
								</Box>
							</Box>
							{/* Row for description */}
							<Box className={classes.row}>
								<RichTextEditor
									size="md"
									label={t('global.description')}
									{...props.form.getInputProps(
										'description'
									)}
									maxWidth={width}
									placeholder={t('global.description')}
								/>
							</Box>
							<Divider size="xs" />
							{/* Row for Buttons */}
							<Box className={classes.rowButtons}>
								<Box
									className={
										classes.containerButtonCancel
									}
								>
									<Button
										fullWidth
										size={isBigScreen ? 'lg' : 'md'}
										variant="transparent"
										onClick={() => {
											props.onCancel();
										}}
									>
										{t('global.cancel')}
									</Button>
								</Box>
								<Box className={classes.containerButton}>
									<Button
										data-autofocus
										fullWidth
										size={isBigScreen ? 'lg' : 'md'}
										onClick={async () => {
											await handleSubmitForm(props.form);
										}}
										loading={
											addMeasurabilityProject.isLoading
										}
									>
										{t(
											'measurabilities.addMeasurabilities'
										)}
									</Button>
								</Box>
							</Box>
						</Stack>
					)}
			</form>
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
